/* eslint-disable max-lines */
/* eslint-disable no-fallthrough */

import { Component, Input, inject } from '@angular/core';
import { Validators } from '@angular/forms';

import { DataReviewService } from 'src/app/data-review/@shared/data-review.service';

import {
  BaseFormComponent,
  DatasetFormCompleteStatus,
  DatasetFormData,
  DatasetFormStatus,
} from 'src/app/@shared/form/base-form.component';
import {
  ContactPerson,
  DynamicFormValues,
  FormConfig,
  FormFieldKey,
  FormFieldList,
  FormFieldValue,
  FormFieldsetConfig,
  SFormConfig,
} from 'src/app/@shared/form/form.types';
import { DATA_REVIEW_FORM_HIDDEN_KEYS, FormKey } from 'src/app/@shared/form/form-config';
import {
  FORM_FIELD_PAIRS,
  FORM_FIELD_SELECT_OPTIONS_SUB_TYPE,
  FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID,
  LOCKED_SEARCH_FORMS_BY_SUB_TYPE_FORM,
  SUB_TYPE_WITH_SUB_TYPE_FORMS,
  SelectOrganizationModalId,
} from 'src/app/@shared/form/form-field-config';
import {
  FORM_KEY_TO_FREE_TEXT_KEY_MAP, FREE_TEXT_FORM_KEYS,
  ORGANIZATION_KEYS_WITH_FREE_TEXT,
  OrganizationModel,
} from 'src/app/organization/@shared/organization.model';
import { FileUploadService } from 'src/app/@core/file-upload.service';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { ModalController, ModalOptions } from '@ionic/angular';
import {
  Organization,
  OrganizationSubType,
  OrganizationSubTypeForm,
  File as ProtoFile,
} from '@common/entities';
import { OrganizationListPage } from 'src/app/organization/organization-list/organization-list.page';
import { OrganizationService } from 'src/app/organization/@shared/organization.service';

import { DynamicFormFieldModel } from 'src/app/@shared/form/dynamic-form-field/dynamic-form-field.model';
import { FreeTextContentMap } from 'src/app/data-review/@shared/data-review.model';
import { OrganizationType } from '@lib/types';
import { PersonListPage } from 'src/app/person/person-list/person-list.page';
import { PersonModel } from 'src/app/person/@shared/person.model';
import { TopicListPage } from 'src/app/topic/topic-list/topic-list.page';
import { TopicModel } from 'src/app/topic/@shared/topic.model';
import {
  createFieldLists,
  createFieldsByConfig,
  createForm,
  getFormKey, getFreeTextContent,
} from 'src/app/@shared/form/form-helper';
import { isEmpty } from 'src/app/@shared/app-helper';
import {
  isOrganizationPublicField,
  resolveOrganizationPublicFieldKey,
} from 'src/app/organization/@shared/organization-public-field-resolver';
import {
  mapContactPersonsFreeTextToContactPersonsRows,
  mapContactPersonsRowsToFreeText,
  sanitizeContactPersonsRows,
} from 'src/app/organization/@shared/contact-persons-review.mapper';
import { personNameValidator } from 'src/app/@shared/person-name.validator';
import { publicFieldsConfig } from '@api/organization/@shared/public-fields.config';

const PUBLIC_FIELDS_CONFIG = publicFieldsConfig;
const DYNAMIC_FREE_TEXT_FORM_KEYS: FormFieldKey[] = ['topicsFreeText', 'contactPersonsFreeText'];
const ORGANIZATION_FREE_TEXT_FORM_KEYS: FormFieldKey[] = ['contactOrganizationFreeText', 'providerFreeText', 'providedByOtherFreeText'];

// Optional explicit subfield visibility for dynamic fields.
// If absent for a dynamic field, visibility is inherited from the parent field.
type DynamicSubFieldPublicConfig = Partial<Record<keyof Organization, string[]>>;
const PUBLIC_DYNAMIC_SUB_FIELDS_CONFIG: DynamicSubFieldPublicConfig = {
  contactPersons: [
    'person',
    'firstName',
    'lastName',
    'phoneNumber',
    'email',
  ],
  emails: ['email'],
};

type SelectModalId = SelectOrganizationModalId | 'person' | 'topics';
type SelectModalItem = OrganizationModel | PersonModel | TopicModel;
type OrganizationReviewRequestPayload = Partial<Organization> & FreeTextContentMap & { deletionReason?: string };

@Component({
  selector: 'organization-form',
  standalone: false,
  styleUrls: ['./organization-form.component.scss'],
  templateUrl: './organization-form.component.html',
})
export class OrganizationFormComponent extends BaseFormComponent<OrganizationModel> {
  @Input() public dataReviewId: string;
  @Input() public changedSubForms: { [key in FormFieldKey]?: boolean[] } = {};
  @Input() public changedSubFormFields: { [key in FormFieldKey]?: Record<string, boolean>[] } = {};
  @Input() public token: string;
  @Input() public isInSplitView: boolean;
  @Input() public freeTextContents: FreeTextContentMap;
  @Input() public formInteractionsDisabled = false;

  public dataReceived: boolean;
  public modalIsOpen: boolean;

  public reviewRequestConditions: boolean[] = [false, false, false];
  public reviewRequestDeleteDataset = false;
  public reviewRequestDeletionReason = '';
  public showHiddenFieldIcon: boolean;

  // Verknüpfungen
  public selectedItems: { [key in SelectModalId]: SelectModalItem[] } = {
    branchOffices: [],
    contactOrganization: [],
    localContactPoint: [],
    mainOffice: [],
    person: [],
    providedByOther: [],
    providedByRed: [],
    provider: [],
    providesFor: [],
    topics: [],
  };

  public isMultiSelect: { [key in SelectModalId]: boolean } = {
    branchOffices: true,
    contactOrganization: false,
    localContactPoint: false,
    mainOffice: false,
    person: false,
    providedByOther: false,
    providedByRed: false,
    provider: false,
    providesFor: true,
    topics: true,
  };

  private lastKnownSubType: OrganizationSubType;

  private dataReviewService = inject(DataReviewService);
  private organizationService = inject(OrganizationService);
  private modalController = inject(ModalController);
  private fileUploadService = inject(FileUploadService);

  private _status: DatasetFormStatus;
  private _changedFields: FormFieldKey[] = [];

  @Input()
  public get changedFields(): FormFieldKey[] {
    return this._changedFields;
  }

  public set changedFields(changedFields: FormFieldKey[]) {
    this._changedFields = Array.isArray(changedFields)
      ? changedFields
      : [];

    this.recomputeSplitViewFreeTextVisibility();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Input()
  public get status(): DatasetFormStatus {
    return this._status;
  }

  public set status(status: DatasetFormStatus) {
    this._status = status;
    this.showHiddenFieldIcon = status === 'review-request';
  }

  public isFormContentBlocked(): boolean {
    return this.formInteractionsDisabled || this._status === 'accepted' || this._status === 'denied' || this.isInternalDeletionReview();
  }

  public isFieldBlocked(fieldId: FormFieldKey): boolean {
    // already blocked, avoid nested is-blocked classes
    if (this.isFormContentBlocked()) {
      return false;
    }

    return FREE_TEXT_FORM_KEYS.includes(fieldId as any) && this._status === 'to-be-reviewed';
  }

  public onValueUpdated(value: FormFieldValue, item: FormFieldModel): void {
    if (item.id === 'subType') {
      this.onOrganizationSubTypeUpdated(value as OrganizationSubType);
    } else if (item.id === 'subTypeForm') {
      this.onOrganizationSubTypeFormUpdated(value as OrganizationSubTypeForm);
    }
  }

  // eslint-disable-next-line max-lines-per-function
  public async openModal(result: { field: FormFieldModel; formGroupIndex?: number }): Promise<void> {
    if (this.formInteractionsDisabled) {
      return Promise.resolve();
    }

    if (this.modalIsOpen) {
      return Promise.resolve();
    }

    this.modalIsOpen = true;

    const { field, formGroupIndex } = result;

    const { type } = this.datasetData;

    let modal: HTMLIonModalElement | null;

    switch (field.id) {
      case 'localContactPoint':
      case 'contactOrganization': // eslint-disable-line padding-line-between-statements
      case 'provider': // eslint-disable-line padding-line-between-statements
      case 'mainOffice': // eslint-disable-line padding-line-between-statements
      case 'branchOffices': // eslint-disable-line padding-line-between-statements
      case 'providedByRed': // eslint-disable-line padding-line-between-statements
      case 'providedByOther': // eslint-disable-line padding-line-between-statements
      case 'providesFor': // eslint-disable-line padding-line-between-statements
        modal = await this.getOrganizationModal(type,
          (this.selectedItems[field.id] as OrganizationModel[]).slice(),
          field.id,
          this.isMultiSelect[field.id]);
        break;

      case 'person':
        modal = await this.getPersonModal(type,
          this.getSelectedPersons(field.id, field.parentId, formGroupIndex),
          this.isMultiSelect[field.id]);
        break;

      case 'topics':
        modal = await this.getTopicModal(type, (this.selectedItems[field.id] as TopicModel[]).slice(), this.isMultiSelect[field.id]);
        break;

      default:
        console.error(`Case for ${field.id} not handled`);
    }

    if (!modal) {
      this.modalIsOpen = false;
      throw new Error('Modal has not been created');
    }

    await modal.present();

    const { data } = await modal.onWillDismiss();

    this.modalIsOpen = false;

    if (!data) {
      return Promise.resolve();
    }

    this.onModalValuesReceived(field, data, formGroupIndex);

    return Promise.resolve();
  }

  public removeItemFromSelectModal(id: SelectModalId, value: string): void {
    if (this.formInteractionsDisabled) {
      return;
    }

    this.selectedItems[id] = this.selectedItems[id].filter((item) => {
      return item.displayName !== value;
    });

    this.editForm.patchValue({
      [id]: this.selectedItems[id].length
        ? this.selectedItems[id]
        : null,
    });

    this.editForm.markAsDirty();

    if (id === 'contactOrganization') {
      this.onContactOrganizationUpdated();
    }

    if (FORM_FIELD_PAIRS.flat().includes(id)) {
      this.onFieldPairUpdated(id);
    }
  }

  public formIsValid(): boolean {
    if (this.status === 'edit') {
      return true;
    }

    if (this.formInteractionsDisabled) {
      return false;
    }

    if (this.status === 'review-request') {
      const hasDeletionReason = !this.reviewRequestDeleteDataset || !!this.reviewRequestDeletionReason.trim();
      const hasAcceptedConditions = this.reviewRequestConditions.every((condition) => {
        return condition;
      });

      if (this.reviewRequestDeleteDataset) {
        return hasAcceptedConditions && hasDeletionReason;
      }

      return hasAcceptedConditions && this.editForm.valid;
    }

    if (this.isInternalDeletionReview()) {
      return true;
    }

    return this.editForm.valid;
  }

  public override submit(isDenied?: boolean): Promise<void> {
    if (this.formInteractionsDisabled) {
      return Promise.resolve();
    }

    return super.submit(isDenied);
  }

  public listHasVisibleItems(list: FormFieldList): boolean {
    if (!list?.items?.length) {
      return false;
    }

    return list.items.some((item) => {
      return !this.hiddenFieldIds.includes(item.id)
        && item.inputType !== 'hidden'
        && !item.isHidden;
    });
  }

  protected onDataReceived(data: OrganizationModel): void {
    this.datasetData = {
      ...data,
    } as OrganizationModel;

    this.lastKnownSubType = data.subType;

    this.formIsBlocked = this.status === 'accepted' || this.status === 'denied';

    this.setHiddenKeys();
    this.prepareForm();

    if (this.showHiddenFieldIcon) {
      this.setFormFieldPublicState();
    }

    this.patchFormByDataset(this.datasetData);

    if (this.isInSplitView || this.status === 'review-request') {
      this.patchFreeTextFields();
    }

    requestAnimationFrame(() => {
      this.dataReceived = true;
    });
  }

  protected onFormPatchedByDataset(): void {
    for (const key of Object.keys(this.selectedItems)) {
      const id = key as SelectModalId;

      // we don't want to run the following for empty arrays (can result in unnecessary warnings for FORM_FIELD_PAIRS)
      if (!isEmpty(this.datasetData[id])) {
        this.selectedItems[id] = Array.isArray(this.datasetData[id])
          ? this.datasetData[id]
          : [this.datasetData[id]];

        if (FORM_FIELD_PAIRS.flat().includes(id)) {
          this.onFieldPairUpdated(id);
        }
      }
    }
  }

  protected setHiddenKeys(): void {
    if (this.status === 'review-request') {
      this.hiddenFieldIds = DATA_REVIEW_FORM_HIDDEN_KEYS;
    }
  }

  protected getFormId(): FormKey {
    return getFormKey(this.datasetData.type, this.datasetData.subType, this.datasetData.subTypeForm);
  }

  // eslint-disable-next-line max-statements,max-lines-per-function
  protected setConfig(config: FormConfig, fieldsetConfig: FormFieldsetConfig): void {
    let modifiedConfig: SFormConfig<FormFieldKey> = {
      ...config,
    };

    modifiedConfig.subTypeForm = {
      ...config.subTypeForm,
    };

    // no final config yet
    if (this.formId === 'selfHelpAddress') {
      modifiedConfig.subType.options = FORM_FIELD_SELECT_OPTIONS_SUB_TYPE;

      modifiedConfig.subTypeForm.options = this.datasetData.subType
        ? FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID[this.datasetData.subType]
        : [];
    }

    if (!modifiedConfig.subTypeForm.options?.length) {
      delete modifiedConfig.subTypeForm;
    }

    if (modifiedConfig.searchForms) {
      const lockedSearchForms = LOCKED_SEARCH_FORMS_BY_SUB_TYPE_FORM[this.formId as OrganizationSubTypeForm];

      if (lockedSearchForms?.length) {
        modifiedConfig.searchForms = {
          ...modifiedConfig.searchForms,
          lockedOptions: lockedSearchForms,
        };
      }
    }

    if (this.isInSplitView || this.status === 'review-request') {
      modifiedConfig = this.getConfigWithFreeTextFields(modifiedConfig);
    }

    // NOTE: Should be done last before creating allFieldConfigs because other config modifications
    // before might have added id's to the hiddenFieldIds array
    if (this.hiddenFieldIds.length) {
      modifiedConfig = this.getVisibleFieldsConfig(modifiedConfig);
    }

    this.allFieldConfigs = createFieldsByConfig(modifiedConfig);
    this.applyContactPersonSubFieldStates();

    const fieldIds = this.allFieldConfigs
      .map((field) => { return field.id; });

    const formData = this.getFormDataWithLockedSearchForms(this.getRestoredFormData(fieldIds));

    this.editForm = createForm(this.formId, this.allFieldConfigs);
    this.editForm.patchValue(formData);

    this.formFieldLists = createFieldLists(this.formId, this.allFieldConfigs, fieldsetConfig);

    this.setFieldListCategories(this.formFieldLists);
  }

  // (1) create organization
  // (2) edit organization
  // (3) request organization
  // (4) accept/deny organization
  // eslint-disable-next-line max-statements, max-lines-per-function,complexity
  protected async handleRequest(formData: DatasetFormData, isDenied?: boolean): Promise<boolean> {
    // extract values that are not part from type: Organization
    // -> solves issue that typescripts compiler does not actually know
    // which values are spread into `data: Partial<Organization>`
    const {
      emailsExtended,
      providedByRed, // eslint-disable-line @typescript-eslint/no-unused-vars
      providedByOther, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...restOfFormData
    } = formData;

    const data: OrganizationReviewRequestPayload = {
      ...restOfFormData,
      emails: emailsExtended?.length
        ? JSON.parse(JSON.stringify(emailsExtended))
        : formData.emails,
    };

    if (Array.isArray(data.contactPersons)) {
      data.contactPersons = sanitizeContactPersonsRows(data.contactPersons) as unknown as Organization['contactPersons'];
    }

    this.insertSelectedItems(data);

    const formFieldKeys = Object.keys(this.editForm.value) as FormFieldKey[];
    const fileUploadKeys: FormFieldKey[] = ['transparencyPdf', 'file'];

    // file upload is available in type 2 (transparencyPdf) and type 3 + 4 (file)
    const fileUploads = fileUploadKeys
      .filter((key) => { return formFieldKeys.includes(key); })
      .map(async (key) => {
        try {
          // TODO: discuss if transparencyPdf also needs status check here
          const { file, fileId } = await this.handleFileByStatus(this.status, key);

          data[key] = file as ProtoFile | null;
          data[`${key}Id`] = fileId;

          if (key === 'transparencyPdf') {
            data.hasTransparencyLabel = !!data[key];
          }
        } catch (error) {
          console.error('fileupload error', key, error);
        }
      });

    await Promise.all(fileUploads);

    // delete free text fields on submit of internal data check (because we only needed them for the changed-organization)
    if (this.status === 'to-be-reviewed') {
      for (const key of FREE_TEXT_FORM_KEYS) {
        delete data[key];
      }
    }

    if (this.status === 'new') {
      return !!await this.organizationService.create(data, this.datasetData.type, this.datasetData.subType);
    }

    if (this.status === 'edit') {
      return !!await this.organizationService.patch(this.datasetData.id, data, this.datasetData.type, this.lastKnownSubType);
    }

    let succeeded: boolean;

    if (this.status === 'review-request') {
      const reviewRequestData: OrganizationReviewRequestPayload = {
        ...data,
        contactPersons: structuredClone(this.datasetData?.contactPersons || []) as Organization['contactPersons'],
        contactPersonsFreeText: mapContactPersonsRowsToFreeText(this.editForm.value.contactPersons || []),
      };

      if (this.hiddenFieldIds.length) {
        for (const key of this.hiddenFieldIds) {
          if (key === 'topics' && Array.isArray(reviewRequestData.topics) && reviewRequestData.topics.length) {
            continue;
          }

          reviewRequestData[key] = this.data[key];
        }
      }

      if (this.reviewRequestDeleteDataset) {
        const deletionReason = this.reviewRequestDeletionReason.trim();

        Object.assign(reviewRequestData, {
          deletionReason,
          isPublished: false,
        });
      } else {
        delete reviewRequestData.deletionReason;
      }

      await this.dataReviewService.updateDatareviewsChangedOrganization(reviewRequestData, this.token);
      succeeded = true;

      return Promise.resolve(succeeded);
    }

    if (this.status === 'to-be-reviewed') {
      const status = isDenied
        ? 'denied'
        : 'accepted';

      if (status === 'accepted') {
        await this.organizationService.patch(this.datasetData.id, data, this.datasetData.type, this.lastKnownSubType);
      }

      await this.dataReviewService.patch(this.dataReviewId, {
        status,
      });

      return Promise.resolve(true);
    }

    return Promise.resolve(false);
  }

  protected onRequestFinished(requestSucceeded: boolean, isDenied?: boolean): void {
    if (!requestSucceeded) {
      this.processCompleted.emit('error');

      return;
    }

    const completedType: DatasetFormCompleteStatus = isDenied
      ? 'denied'
      : 'submitted';

    this.processCompleted.emit(completedType);
  }

  private insertSelectedItems(data: Partial<Organization>): void {
    const keysToSkip: (keyof typeof this.selectedItems)[] = ['providedByRed', 'providedByOther'];

    // There can only be 1 provider -> so it's either red or the other
    // The UI should make sure, there can only be one field filled at a time
    const [selectedProvidedByItem] = this.selectedItems.providedByRed?.length
      ? this.selectedItems.providedByRed
      : this.selectedItems.providedByOther;

    data.providedById = selectedProvidedByItem?.id || null;

    // handle form 1A mainOffice
    const [selectedMainOfficeItem] = this.selectedItems.mainOffice;

    data.mainOfficeId = selectedMainOfficeItem?.id || null;
    data.isMainOffice = !!this.selectedItems.branchOffices.length;

    Object.keys(this.selectedItems)
      .filter((key: keyof typeof this.selectedItems) => { return !keysToSkip.includes(key); })
      .forEach((key) => {
        const items = this.selectedItems[key];

        if (items.length) {
          if (this.isMultiSelect[key] === false) {
            data[key] = {
              id: items[0].id,
            };
          } else if (key === 'topics') {
            data[key] = items.map((i) => {
              return {
                topicId: i.id,
              };
            });
          } else {
            data[key] = items.map((i) => {
              return {
                id: i.id,
              };
            });
          }
        } else {
          data[key] = this.isMultiSelect[key]
            ? []
            : null;
        }
      });
  }

  private async getOrganizationModal(formType: OrganizationType,
    selectedItems: OrganizationModel[],
    organizationFieldId: SelectOrganizationModalId,
    isMultiSelect: boolean = false): Promise<HTMLIonModalElement> {
    const {
      organizationType,
      organizationSubType,
      organizationSubTypeForm,
    } = this.getOrganizationFilterByField(formType, organizationFieldId);

    const componentProps = {
      disabledItemIds: [this.datasetData?.id],
      formType: organizationType,
      isModal: true,
      isMultiSelect,
      organizationSubType,
      organizationSubTypeForm,
      selectedItems,
    };

    const options: ModalOptions = {
      component: OrganizationListPage,
      componentProps,
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    const modal = await this.modalController.create(options);

    options.componentProps.presentingElement = modal;

    return modal;
  }

  private getOrganizationFilterByField(formType: OrganizationType,
    organizationFieldId: SelectOrganizationModalId): {
      organizationType: OrganizationType;
      organizationSubType: OrganizationSubType;
      organizationSubTypeForm: OrganizationSubTypeForm;
    } {
    let organizationType = formType;
    let organizationSubType: OrganizationSubType = null;
    let organizationSubTypeForm: OrganizationSubTypeForm = null;

    if (organizationFieldId === 'providedByOther') {
      // only provide type type 7 addresses
      organizationType = 'otherAddress';
    } else if (organizationFieldId === 'providesFor' || organizationFieldId === 'providedByRed') {
      // only provide type type 1 addresses
      organizationType = 'selfHelpAddress';
      organizationSubType = 'selfHelpAddressRa';
    } else if (organizationFieldId === 'mainOffice' || organizationFieldId === 'branchOffices' || organizationFieldId === 'localContactPoint') {
      // only provide type 1A addresses
      organizationType = 'selfHelpAddress';
      organizationSubType = 'selfHelpAddressRa';
      organizationSubTypeForm = 'selfHelpAddressRa1a';
    } else if (organizationFieldId === 'contactOrganization' || organizationFieldId === 'provider') {
      // only provide type 2 (green) addresses
      organizationType = 'selfHelpAddress';
      organizationSubType = 'selfHelpAddressGa';
    }

    return {
      organizationSubType,
      organizationSubTypeForm,
      organizationType,
    };
  }

  // single person for contactPersons
  private getSelectedPersons(fieldId: FormFieldKey, parentId: FormFieldKey, formGroupIndex = 0): PersonModel[] {
    if (parentId === 'contactPersons') {
      const selectedContactPerson = this.editForm.value[parentId]?.[formGroupIndex];

      return selectedContactPerson?.[fieldId]
        ? [selectedContactPerson[fieldId]]
        : [];
    }

    return (this.selectedItems[fieldId] as PersonModel[]).slice();
  }

  private async getPersonModal(type: string, selectedItems: PersonModel[], isMultiSelect: boolean = false): Promise<HTMLIonModalElement> {
    const componentProps = {
      formType: type,
      isModal: true,
      isMultiSelect,
      selectedItems,
    };

    const options: ModalOptions = {
      component: PersonListPage,
      componentProps,
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    const modal = await this.modalController.create(options);

    options.componentProps.presentingElement = modal;

    return modal;
  }

  private async getTopicModal(type: string, selectedItems: TopicModel[], isMultiSelect: boolean = false): Promise<HTMLIonModalElement> {
    const componentProps = {
      formType: type,
      isModal: true,
      isMultiSelect,
      selectedItems,
    };

    const options: ModalOptions = {
      component: TopicListPage,
      componentProps,
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    const modal = await this.modalController.create(options);

    options.componentProps.presentingElement = modal;

    return modal;
  }

  private onOrganizationSubTypeUpdated(value: OrganizationSubType): void {
    this.datasetData.subType = value;
    this.datasetData.subTypeForm = null;

    this.prepareForm();

    if (!SUB_TYPE_WITH_SUB_TYPE_FORMS.includes(value)) {
      this.editForm.markAsDirty();
    }
  }

  private onOrganizationSubTypeFormUpdated(value: OrganizationSubTypeForm): void {
    if (this.editForm?.controls?.searchForms) {
      this.editForm.patchValue({
        searchForms: [],
      });
    }

    this.datasetData.subTypeForm = value;

    this.prepareForm();
    this.editForm.markAsDirty();
  }

  private getFormDataWithLockedSearchForms(formData: DatasetFormData): DatasetFormData {
    const lockedSearchForms = LOCKED_SEARCH_FORMS_BY_SUB_TYPE_FORM[this.formId as OrganizationSubTypeForm];

    if (!lockedSearchForms?.length) {
      return formData;
    }

    const selectedSearchForms = Array.isArray(formData.searchForms)
      ? formData.searchForms
      : [];

    return {
      ...formData,
      searchForms: Array.from(new Set([...selectedSearchForms, ...lockedSearchForms])),
    };
  }

  // when changing subType/subForm keep the current form data
  private getRestoredFormData(fieldIds: FormFieldKey[]): DatasetFormData {
    const previousFormData = {
      ...this.editForm.value,
      subType: this.datasetData.subType,
      subTypeForm: this.datasetData.subTypeForm,
    };

    const formData: DatasetFormData = {};

    for (const key of fieldIds) {
      // skip for null and undefined
      if (this.isValidValue(previousFormData[key])) {
        formData[key] = previousFormData[key];
      }
    }

    return formData;
  }

  // editForm[key] only contains the formatted value
  private onModalValuesReceived(field: FormFieldModel, values: SelectModalItem[], formGroupIndex?: number): void {
    console.info('onModalValuesReceived', field, values, formGroupIndex);
    this.editForm.markAsDirty();

    if (field.parentId === 'contactPersons' && field.id === 'person') {
      this.patchPersonInContactField(field.parentId, values as PersonModel[], formGroupIndex);

      return;
    }

    if (field.id === 'contactOrganization') {
      this.onContactOrganizationUpdated(values as OrganizationModel[]);
    }

    this.selectedItems[field.id] = values;

    if (FORM_FIELD_PAIRS.flat().includes(field.id)) {
      this.onFieldPairUpdated(field.id);
    }

    // TODO: or update this.dynamicFields[field.id] which should also patch form values
    //  (same approach as in patchPersonInContactField)
    this.editForm.patchValue({
      [field.id]: values,
    });
  }

  // dynamic field
  // after we've received our `person` value from select modal, we need to update personId in contactPerson as well
  private patchPersonInContactField(id: FormFieldKey, values: PersonModel[], formGroupIndex = 0): void {
    const [person] = values;
    const contactPersons = this.asFormArray(id).getRawValue() as ContactPerson[];

    this.dynamicFields[id] = contactPersons.map((field: ContactPerson, index) => {
      if (index !== formGroupIndex) {
        return field;
      }

      return {
        ...field,
        person,
        personId: person.id,
      };
    });
  }

  // provider should contain the same selection as contactOrganization (if contactOrganization is set)
  private onContactOrganizationUpdated(values?: OrganizationModel[]): void {
    const providerField = this.getFieldById('provider');

    // we have contactOrganization but no provider field in form type 4. (we only have both fields in form 3)
    if (!providerField) {
      return;
    }

    providerField.isReadOnly = !!values?.length;

    if (!values?.length) {
      return;
    }

    this.selectedItems.provider = values;
    this.editForm.patchValue({
      provider: this.selectedItems.provider,
    });
  }

  // eslint-disable-next-line max-statements
  private onFieldPairUpdated(fieldId: FormFieldKey): void {
    const keyPair = FORM_FIELD_PAIRS
      .find(([first, second]) => { return first === fieldId || second === fieldId; });

    if (!keyPair) {
      console.error(`related field for id ${fieldId} is not defined`);

      return;
    }

    if (this.isMainOfficePair(keyPair)) {
      this.updateMainOfficePairReadOnlyState();

      return;
    }

    const isProvidedByPair = keyPair.includes('providedByRed') && keyPair.includes('providedByOther');

    // On external review we allow both fields to stay editable.
    if (this.status === 'review-request' && isProvidedByPair) {
      const providedByRedField = this.getFieldById('providedByRed');
      const providedByOtherField = this.getFieldById('providedByOther');

      if (providedByRedField) {
        providedByRedField.isReadOnly = false;
      }

      if (providedByOtherField) {
        providedByOtherField.isReadOnly = false;
      }

      return;
    }

    const relatedFieldId = keyPair[0] === fieldId
      ? keyPair[1]
      : keyPair[0];

    const relatedField = this.getFieldById(relatedFieldId);

    if (!relatedField) {
      console.warn(`Field with id ${relatedFieldId} not found`);

      return;
    }

    relatedField.isReadOnly = !!this.selectedItems[fieldId].length;
  }

  private isMainOfficePair(fieldPair: [FormFieldKey, FormFieldKey]): boolean {
    return fieldPair.includes('mainOffice') && fieldPair.includes('branchOffices');
  }

  private updateMainOfficePairReadOnlyState(): void {
    const mainOfficeField = this.getFieldById('mainOffice');
    const branchOfficesField = this.getFieldById('branchOffices');

    if (!mainOfficeField || !branchOfficesField) {
      console.warn('Field pair mainOffice/branchOffices not found');

      return;
    }

    const hasMainOffice = !!this.selectedItems.mainOffice.length;
    const hasBranchOffices = !!this.selectedItems.branchOffices.length;

    // imported data can contain both values; in this recovery state both fields must stay editable
    mainOfficeField.isReadOnly = hasBranchOffices && !hasMainOffice;
    branchOfficesField.isReadOnly = hasMainOffice && !hasBranchOffices;
  }

  private getFieldById(id: FormFieldKey): FormFieldModel | null {
    let field: FormFieldModel | null;

    for (const list of this.formFieldLists) {
      field = list.items.find((item: (FormFieldModel | DynamicFormFieldModel)) => { return item.id === id; });

      if (field) {
        break;
      }
    }

    return field;
  }

  private applyContactPersonSubFieldStates(): void {
    const contactPersonsField = this.allFieldConfigs.find((field) => {
      return field.id === 'contactPersons' && field.type === 'dynamic';
    }) as DynamicFormFieldModel;

    if (!contactPersonsField?.dynamicFieldConfig?.fields?.length) {
      return;
    }

    const isReviewRequest = this.status === 'review-request';
    const showNameHelpers = isReviewRequest || this.status === 'to-be-reviewed' || this.isInSplitView;

    contactPersonsField.dynamicFieldConfig.groupValidators = isReviewRequest
      ? [personNameValidator]
      : [];

    for (const subField of contactPersonsField.dynamicFieldConfig.fields) {
      if (subField.id === 'firstName' || subField.id === 'lastName') {
        subField.isHidden = !showNameHelpers;
        subField.isReadOnly = this.status === 'to-be-reviewed';
        subField.isRequired = false;

        const validatorsWithoutRequired = subField.validators.filter((validator) => {
          return validator !== Validators.required;
        });

        subField.validators = validatorsWithoutRequired;
      }

      if (subField.id === 'person') {
        subField.isHidden = isReviewRequest;
        subField.isRequired = !isReviewRequest;

        const validatorsWithoutRequired = subField.validators.filter((validator) => {
          return validator !== Validators.required;
        });

        subField.validators = isReviewRequest
          ? validatorsWithoutRequired
          : [Validators.required, ...validatorsWithoutRequired];
      }
    }
  }

  private isInternalDeletionReview(): boolean {
    return this.status === 'to-be-reviewed'
      && !!this.data?.deletionReason
      && this.data?.isPublished === false;
  }

  private recomputeSplitViewFreeTextVisibility(): void {
    if (!this.isInSplitView || !this.datasetData?.type || !this.allFieldConfigs.length) {
      return;
    }

    this.prepareForm();
    this.patchFormByDataset(this.datasetData);
    this.patchFreeTextFields();
  }

  private getVisibleFieldsConfig(config: FormConfig): FormConfig {
    const newConfig: FormConfig = {};

    for (const key of Object.keys(config) as FormFieldKey[]) {
      if (!this.hiddenFieldIds.includes(key)) {
        // create shallow copy of config but since were not altering any values, it should be no problem
        newConfig[key] = config[key];
      }
    }

    return newConfig;
  }

  private async handleFileByStatus(status: DatasetFormStatus, key: FormFieldKey):
  Promise<{ file: { id: string } | null; fileId: string | null }> {
    let fileId: string | null = null;

    switch (status) {
      case 'new':

      case 'edit':
        fileId = await this.fileUploadService.manageFiles(this.datasetData[key], this.editForm.value[key]);
        break;

      case 'to-be-reviewed': {
        const dataReview = this.dataReviewService.getStoreItem(this.dataReviewId);
        const { organization, changedOrganization } = dataReview;
        const finalChangedOrganization = this.editForm.value;

        fileId = await this.organizationService
          .resolveReviewedFile(organization[key], changedOrganization[key], finalChangedOrganization[key]);
      }
        break;

      case 'review-request':
        fileId = await this.organizationService.handleReviewRequestFile(this.datasetData[key], this.editForm.value[key]);
        break;

      default:
        console.warn('[OrganizationFormComponent] handleFileByStatus called with unsupported status:', status);
        break;
    }

    return {
      file: fileId
        ? {
          id: fileId,
        } as ProtoFile
        : null,
      fileId,
    };
  }

  private setFormFieldPublicState(): void {
    const configKey = this.datasetData.subType || this.datasetData.type;
    const publicFields: (keyof Organization)[] = PUBLIC_FIELDS_CONFIG[configKey] || [];
    const publicFieldIds = new Set(publicFields);

    if (!publicFields.length) {
      console.warn(`No public fields configuration found for organization with configKey: ${configKey}`);
    }

    for (const formField of this.allFieldConfigs) {
      const isFieldNotPublic = !isOrganizationPublicField(formField.id, publicFieldIds);

      if (formField.type === 'dynamic') {
        const canonicalFieldId = resolveOrganizationPublicFieldKey(formField.id);
        const explicitPublicSubFields = PUBLIC_DYNAMIC_SUB_FIELDS_CONFIG[canonicalFieldId];

        for (const subField of (formField as DynamicFormFieldModel).dynamicFieldConfig.fields) {
          subField.isNotPublic = explicitPublicSubFields
            ? isFieldNotPublic || !explicitPublicSubFields.includes(subField.id)
            : isFieldNotPublic;
        }

        continue;
      }

      formField.isNotPublic = isFieldNotPublic;
    }
  }

  // eslint-disable-next-line max-statements,max-lines-per-function
  private getConfigWithFreeTextFields(config: FormConfig): FormConfig {
    // on external review page, we only want to show the free text fields
    // in contrast e.g. on internal review (comparison page), we want to show both fields
    const hideOriginalFields = this.status === 'review-request';
    const newConfig: FormConfig = structuredClone(config); // deep copy

    for (const key of ORGANIZATION_KEYS_WITH_FREE_TEXT) {
      if (key === 'contactPersons') {
        this.hiddenFieldIds = this.hiddenFieldIds.filter((id) => {
          return id !== 'contactPersonsFreeText';
        });

        continue;
      }

      const originalField = newConfig[key];

      if (!originalField) {
        // do not add free text field for ${key} since it does not exist in this form config
        continue;
      }

      const freeTextKey = FORM_KEY_TO_FREE_TEXT_KEY_MAP[key];
      const isDynamicFreeText = DYNAMIC_FREE_TEXT_FORM_KEYS.includes(freeTextKey);

      const freeTextFieldConfig: FormConfig[string] = {
        ...originalField,
        hasCustomErrorText: true,
        hasHint: true, // since these text fields deviate from their original fields, we want to show a hint
        type: isDynamicFreeText
          ? 'dynamic'
          : 'simple',
      };

      if (!isDynamicFreeText) {
        freeTextFieldConfig.inputType = ORGANIZATION_FREE_TEXT_FORM_KEYS.includes(freeTextKey)
          ? 'textarea'
          : 'text';
        freeTextFieldConfig.options = undefined; // eslint-disable-line no-undefined
        freeTextFieldConfig.hasMultipleValues = undefined; // eslint-disable-line no-undefined
      }

      newConfig[freeTextKey] = freeTextFieldConfig;

      if (hideOriginalFields) {
        this.hiddenFieldIds.push(key);
      }

      // On split-view comparison, keep free-text mirror visibility deterministic:
      // hide unchanged mirrors, show changed mirrors.
      // We must reset any prior hidden state here because this method can run multiple times.
      this.hiddenFieldIds = this.hiddenFieldIds.filter((id) => {
        return id !== freeTextKey;
      });

      if (this.isInSplitView && !this.changedFields.includes(freeTextKey)) {
        this.hiddenFieldIds.push(freeTextKey);
      }
    }

    return newConfig;
  }

  // eslint-disable-next-line max-lines-per-function,max-statements
  private patchFreeTextFields(): void {
    // on comparison data-check, we need to patch the free-text-fields with contents from changed-organization
    if (this.status === 'to-be-reviewed') {
      if (!this.freeTextContents) {
        return;
      }

      for (const key of FREE_TEXT_FORM_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(this.freeTextContents, key)) {
          continue;
        }

        const value = this.getNormalizedFreeTextContent(key, this.freeTextContents[key]);

        if (key === 'contactPersonsFreeText' && Array.isArray(value)) {
          this.patchOrClearDynamicDatasetData('contactPersons', this.getContactPersonsRowsFromFreeText(value));

          continue;
        }

        if (DYNAMIC_FREE_TEXT_FORM_KEYS.includes(key) && Array.isArray(value)) {
          this.patchOrClearDynamicDatasetData(key, value);
        } else {
          this.editForm.patchValue({
            [key]: value,
          });
        }
      }

      return;
    }

    // on external review page, we need to patch the free-text-fields with contents from the current form values
    for (const key of ORGANIZATION_KEYS_WITH_FREE_TEXT) {
      const freeTextKey = FORM_KEY_TO_FREE_TEXT_KEY_MAP[key];
      const formValue = this.datasetData[key];

      if (!formValue) {
        continue;
      }

      const value = this.getNormalizedFreeTextContent(freeTextKey, getFreeTextContent(formValue, key));

      if (key === 'contactPersons' && Array.isArray(value)) {
        this.patchDynamicDatasetData(key, this.getContactPersonsRowsFromFreeText(value));

        continue;
      }

      if (DYNAMIC_FREE_TEXT_FORM_KEYS.includes(freeTextKey) && Array.isArray(value)) {
        this.patchDynamicDatasetData(freeTextKey, value);
      } else {
        this.editForm.patchValue({
          [freeTextKey]: value,
        });
      }
    }
  }

  private patchOrClearDynamicDatasetData(key: FormFieldKey, values: DynamicFormValues[]): void {
    if (values?.length) {
      this.patchDynamicDatasetData(key, values);

      return;
    }

    this.dynamicFields[key] = [];
    this.asFormArray(key).clear();
  }

  private getNormalizedFreeTextContent(key: FormFieldKey, value: unknown): unknown {
    if (!DYNAMIC_FREE_TEXT_FORM_KEYS.includes(key)) {
      return value;
    }

    if (Array.isArray(value)) {
      return value;
    }

    return [];
  }

  private getContactPersonsRowsFromFreeText(value: unknown[]): DynamicFormValues[] {
    return mapContactPersonsFreeTextToContactPersonsRows(value, this.datasetData?.contactPersons || []);
  }
}
